import { ThisConnector } from '../../../api/connector'
import { ContrastQueryParameters } from '../../../graphql/schema/endpoint-helpers'
import { GradeValue } from '../../../graphql/schema/types/grade-value'
import { AgentLanguageDisplay } from '../../agent-language/agent-language'
import { Query } from '../../../hooks/queries'
import { QueryClient } from 'react-query'
import { Role } from '@contrast-security-inc/base-types'
import { transformRole } from '../../../graphql/schema/queries/helpers/role'

export interface ApplicationRouteBreakdown {
  discovered: number
  exercised: number
}

interface ApplicationModuleScoresApi {
  grade: number
  letter_grade: GradeValue
}

export interface ApplicationModule {
  uuid: string
  name: string
  primary: boolean
  language: AgentLanguageDisplay
  status: 'online' | 'offline'
  lastSeen: string
  routes: ApplicationRouteBreakdown
  grade: number
  letterGrade: GradeValue
  role: Role
}

export interface ApplicationModuleApi {
  app_id: string
  name: string
  primary: boolean
  language: AgentLanguageDisplay
  status: 'online' | 'offline'
  last_seen: string
  routes: ApplicationRouteBreakdown
  scores: ApplicationModuleScoresApi
  roles: Role[]
}

export interface ApplicationModulesResponse {
  applications: ApplicationModule[]
}

interface ApplicationModulesApiResponse {
  applications: ApplicationModuleApi[]
  parentApplication: ApplicationModuleApi
}

export interface GetApplicationModulesArgs {
  organizationUuid: string
  applicationUuid: string
}

function mapApplicationModule({
  app_id,
  name,
  primary,
  language,
  status,
  last_seen,
  routes,
  roles,
  scores: { grade, letter_grade }
}: ApplicationModuleApi): ApplicationModule {
  return {
    uuid: app_id,
    name,
    primary,
    language,
    status,
    lastSeen: last_seen,
    routes,
    role: transformRole(roles) as Role,
    grade,
    letterGrade: letter_grade
  }
}

export async function getApplicationModules(
  this: ThisConnector,
  { organizationUuid, applicationUuid }: GetApplicationModulesArgs
): Promise<ApplicationModulesResponse> {
  const params = new ContrastQueryParameters()
  params.append('expand', 'coverage,scores')

  const { applications, parentApplication } =
    await this.connector.get<ApplicationModulesApiResponse>(
      `/${organizationUuid}/modules/${applicationUuid}?${params}`
    )

  return {
    applications: [parentApplication, ...applications].map(mapApplicationModule)
  }
}

export function invalidateApplicationModuleQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries([Query.APPLICATION_MODULES], {
    exact: false
  })
}
